<template>
  <InputSkeleton :name="name"
                 :errors="errors"
                 class="NumberInput"
                 :class="{
                   [`NumberInput--${size}`]: !!size,
                   'NumberInput--disabled': disabled,
                   'NumberInput--readonly': readonly,
                   'NumberInput--invalid': invalid || !!errors.length,
                   'NumberInput--highlight': isHighlighted,
                   'NumberInput--float-label': floatLabel,
                 }">
    <template v-if="$slots.default || label" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <div class="NumberInput__input">
      <input :id="name"
             :value="valueWithUserDecimalChar"
             type="text"
             data-test-id="input"
             :name="name"
             :autocomplete="autocomplete"
             :autofocus="autofocus"
             :placeholder="placeholder"
             :disabled="disabled"
             :readonly="readonly"
             :step="step"
             :min="min"
             :max="max"
             @input="onInput"
             @keydown="onKeydown"
      />
    </div>
  </InputSkeleton>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
  import { UiSize } from '../../../constants';
  import InputSkeleton from '../InputSkeleton.vue';
  import { InputErrorObject } from './types';

  const props = withDefaults(defineProps<{
    modelValue: number | null;
    name: string;
    label?: string;
    size?: UiSize;
    placeholder?: string;
    autocomplete?: 'on' | 'off';
    autofocus?: boolean;
    invalid?: boolean;
    errors?: InputErrorObject[];
    disabled?: boolean;
    readonly?: boolean;
    highlight?: boolean;
    step?: 'any' | number;
    min?: number;
    max?: number;
    maxDecimals?: number;
    floatLabel?: boolean;
  }>(), {
    size: UiSize.MD,
    autocomplete: 'off',
    errors: () => [],
    min: 0,
    step: 1,
    floatLabel: false,
    placeholder: '',
    maxDecimals: 0,
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: number): void;
  }>();

  const isHighlighted = computed(() => Boolean(props.highlight && !props.modelValue));

  const lastDecimalChar = ref('.');
  const valueWithUserDecimalChar = computed(() => {
    return props.modelValue
      ? `${props.modelValue}`.replace('.', lastDecimalChar.value)
      : '';
  });

  const onInput = (event: InputEvent) => {
    const target = event.target as HTMLInputElement;
    let val = target.value;

    if (props.maxDecimals > 0) {
      // Оставляем только цифры, точку и запятую
      val = val.replace(/[^0-9.,]/g, '');

      // Если первым символом введена точка или запятая считаем что перед ней стоит 0
      if (val && (val.startsWith('.') || val.startsWith(','))) {
        val = `0${val}`;
      }

      // Определяем последний введённый разделитель
      if (val.includes(',')) {
        lastDecimalChar.value = ',';
      } else if (val.includes('.')) {
        lastDecimalChar.value = '.';
      }

      if (val.includes('.') || val.includes(',')) {
        const decimals = val.split(/[.,]/);

        if (decimals.length) {
          const int = decimals.shift();
          const decimal = decimals.join('').slice(0, props.maxDecimals);
          val = `${int}.${decimal}`;
        }
      }

      target.value = val.replace('.', lastDecimalChar.value);
    } else {
      val = val.replace(/[^0-9]/g, '');
      target.value = val;
    }

    emit('update:model-value', Number(val) || null);
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }

    event.preventDefault();

    let currentNumber = Number.parseFloat(`${props.modelValue}`);
    if (Number.isNaN(currentNumber)) {
      currentNumber = props.min !== undefined ? props.min : 0;
    }

    let value;
    if (event.key === 'ArrowUp' && props.step) {
      value = currentNumber + (props.step as number);

      if (props.max !== undefined && value > props.max) {
        value = props.max;
      }
    }

    if (event.key === 'ArrowDown') {
      value = currentNumber - (props.step as number);

      if (props.min !== undefined && value < props.min) {
        value = props.min;
      }
    }

    (event.target as HTMLInputElement).value = value;
    emit('update:model-value', Number(value) || null);
  };
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .NumberInput {
    display: flex;
    flex-direction: column;
    width: 100%;

    &__input {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 100%;
    }

    &__label {
      display: block;
      margin-bottom: 10px;
      font-weight: 600;
    }

    label {
      white-space: nowrap;
    }

    input {
      @include transition($transition-function, $transition-duration, border-color);

      width: 100%;
      border: 1px solid $color-grey-75;
      border-radius: 4px;
      background-color: $color-white;
      line-height: 1;
      font-size: 14px;
      appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
      }
    }

    &--sm input {
      // ...
    }

    &--md input {
      height: 30px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--lg input {
      height: 40px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--xl input {
      height: 50px;
      padding-left: 15px;
      padding-right: 15px;
    }

    &--highlight input {
      background-color: #ffffd2;
    }

    &--disabled input {
      background-color: $color-grey-50;
    }

    &--disabled input {
      cursor: not-allowed;
    }

    input:focus {
      outline: none;
      border-color: $color-second;
    }

    input::placeholder {
      color: $color-grey-100;
    }

    &--invalid input {
      border-color: $color-red-100;
    }

    &--invalid input::placeholder {
      color: $color-red-100;
    }

    &--invalid .svg-icon {
      color: $color-red-100;
    }

    &--float-label {
      @include float-label('NumberInput');
    }
  }
</style>
